export function initBurgerMenu() {
    const burger = document.querySelector('.bt-burger');
    const menu = document.getElementById('bt-mobile-menu');

    if (!burger || !menu) return;

    const surface = menu.querySelector('.bt-mobile-menu__surface');
    const closeEls = menu.querySelectorAll('[data-bt-menu-close="1"]');
    const mobileNav = menu.querySelector('[data-mobile-nav]');
    const gestureArea = surface;
    const views = Array.from(menu.querySelectorAll('[data-nav-view]'));
    const openSubmenuButtons = Array.from(menu.querySelectorAll('[data-open-submenu]'));
    const backButtons = Array.from(document.querySelectorAll('[data-back-to-root="1"]'));
    const topBackButton = document.querySelector('.bt-header__back');

    let lastFocus = null;
    let scrollY = 0;
    let currentView = 'root';

    let openFallbackTimer = null;
    let closeFallbackTimer = null;
    let switchTimer = null;
    let contentReadyRaf1 = null;
    let contentReadyRaf2 = null;

    let touchStartX = 0;
    let touchStartY = 0;
    let touchCurrentX = 0;
    let touchTracking = false;
    let touchLockedAxis = null;

    const isDesktop = () => window.innerWidth > 1100;

    const isOpenLike = () =>
        document.body.classList.contains('bt-nav-opening') ||
        document.body.classList.contains('bt-nav-opened') ||
        document.body.classList.contains('bt-nav-closing');

    const getView = (name) =>
        views.find((view) => view.getAttribute('data-nav-view') === name);

    const clearTimers = () => {
        if (openFallbackTimer) clearTimeout(openFallbackTimer);
        if (closeFallbackTimer) clearTimeout(closeFallbackTimer);
        if (switchTimer) clearTimeout(switchTimer);
        if (contentReadyRaf1) cancelAnimationFrame(contentReadyRaf1);
        if (contentReadyRaf2) cancelAnimationFrame(contentReadyRaf2);

        openFallbackTimer = null;
        closeFallbackTimer = null;
        switchTimer = null;
        contentReadyRaf1 = null;
        contentReadyRaf2 = null;
    };

    const syncBackButton = (forceHidden = false) => {
        if (!topBackButton) return;

        const isRoot = forceHidden || currentView === 'root';
        topBackButton.classList.toggle('is-visible', !isRoot);
        topBackButton.setAttribute('aria-hidden', isRoot ? 'true' : 'false');
        topBackButton.tabIndex = isRoot ? -1 : 0;
    };

    const lockBody = () => {
        scrollY = window.scrollY || window.pageYOffset || 0;
        document.documentElement.classList.add('bt-nav-lock');
        document.body.classList.add('bt-nav-lock');
        document.body.style.top = `-${scrollY}px`;
    };

    const unlockBody = () => {
        document.documentElement.classList.remove('bt-nav-lock');
        document.body.classList.remove('bt-nav-lock');
        document.body.style.top = '';
        window.scrollTo(0, scrollY);
    };

    const removeStateClasses = () => {
        document.body.classList.remove(
            'bt-nav-opening',
            'bt-nav-opened',
            'bt-nav-closing',
            'bt-nav-content-ready'
        );
    };

    const resetSwipeState = () => {
        touchTracking = false;
        touchLockedAxis = null;
        touchStartX = 0;
        touchStartY = 0;
        touchCurrentX = 0;

        if (mobileNav) {
            mobileNav.style.removeProperty('--bt-swipe-shift');
            mobileNav.classList.remove('is-swipe-dragging');
        }
    };

    const resetViews = () => {
        views.forEach((view) => {
            view.classList.remove('is-active', 'is-leaving', 'is-entering');
            view.style.removeProperty('transform');
        });

        const root = getView('root');
        if (root) root.classList.add('is-active');

        currentView = 'root';

        if (mobileNav) {
            mobileNav.setAttribute('data-nav-motion', 'none');
            mobileNav.style.removeProperty('--bt-swipe-shift');
            mobileNav.classList.remove('is-swipe-dragging');
        }

        syncBackButton();
    };

    const setMenuHidden = (hidden) => {
        menu.setAttribute('aria-hidden', hidden ? 'true' : 'false');
        burger.setAttribute('aria-expanded', hidden ? 'false' : 'true');
    };

    const markOpened = () => {
        if (!document.body.classList.contains('bt-nav-opening')) return;
        document.body.classList.remove('bt-nav-opening');
        document.body.classList.add('bt-nav-opened');
    };

    const finalizeClose = () => {
        burger.classList.remove('is-active');
        removeStateClasses();
        unlockBody();
        resetSwipeState();
        resetViews();
        setMenuHidden(true);

        if (lastFocus && typeof lastFocus.focus === 'function') {
            lastFocus.focus();
        }
    };

    const armContentReady = () => {
        document.body.classList.remove('bt-nav-content-ready');

        contentReadyRaf1 = requestAnimationFrame(() => {
            contentReadyRaf2 = requestAnimationFrame(() => {
                if (
                    document.body.classList.contains('bt-nav-opening') ||
                    document.body.classList.contains('bt-nav-opened')
                ) {
                    document.body.classList.add('bt-nav-content-ready');
                }
            });
        });
    };

    const openMenu = () => {
        if (isOpenLike()) return;

        clearTimers();
        lastFocus = document.activeElement;

        lockBody();
        resetViews();
        removeStateClasses();
        setMenuHidden(false);

        burger.classList.add('is-active');
        document.body.classList.add('bt-nav-opening');
        armContentReady();

        openFallbackTimer = window.setTimeout(markOpened, 520);
    };

    const closeMenu = () => {
        if (!isOpenLike()) return;

        clearTimers();
        resetSwipeState();

        const activeView = getView(currentView);
        if (activeView) {
            activeView.classList.add('is-leaving');
        }

        burger.classList.remove('is-active');
        document.body.classList.remove('bt-nav-content-ready');
        document.body.classList.remove('bt-nav-opening', 'bt-nav-opened');
        document.body.classList.add('bt-nav-closing');
        syncBackButton(true);

        closeFallbackTimer = window.setTimeout(finalizeClose, 420);
    };

    const switchView = (nextName) => {
        if (currentView === nextName) return;

        const current = getView(currentView);
        const next = getView(nextName);

        if (!current || !next) return;

        if (switchTimer) clearTimeout(switchTimer);

        resetSwipeState();

        if (mobileNav) {
            const motion = nextName === 'root' ? 'back' : 'forward';
            mobileNav.setAttribute('data-nav-motion', motion);
        }

        current.classList.add('is-leaving');
        next.classList.add('is-entering', 'is-active');

        switchTimer = window.setTimeout(() => {
            current.classList.remove('is-active', 'is-leaving');
            next.classList.remove('is-entering');
            current.style.removeProperty('transform');
            next.style.removeProperty('transform');

            currentView = nextName;
            syncBackButton();

            document.body.classList.remove('bt-nav-content-ready');
            armContentReady();
        }, 260);
    };

    const goBackToRoot = () => {
        if (currentView === 'root') return;
        switchView('root');
    };

    burger.addEventListener('click', () => {
        if (isOpenLike()) {
            closeMenu();
        } else {
            openMenu();
        }
    });

    closeEls.forEach((el) => {
        el.addEventListener('click', closeMenu);
    });

    openSubmenuButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
            const target = btn.getAttribute('data-open-submenu');
            if (!target) return;
            switchView(target);
        });
    });

    backButtons.forEach((btn) => {
        btn.addEventListener('click', goBackToRoot);
    });

    menu.addEventListener('click', (e) => {
        const a = e.target.closest('a');
        if (a) closeMenu();
    });

    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') closeMenu();
    });

    if (surface) {
        surface.addEventListener('transitionend', (e) => {
            if (e.propertyName !== 'transform') return;

            if (document.body.classList.contains('bt-nav-opening')) {
                markOpened();
            }

            if (document.body.classList.contains('bt-nav-closing')) {
                finalizeClose();
            }
        });
    }

    window.addEventListener('resize', () => {
        if (isDesktop() && isOpenLike()) {
            finalizeClose();
        }
    });

    if (gestureArea && mobileNav) {
        gestureArea.addEventListener(
            'touchstart',
            (e) => {
                if (isDesktop()) return;
                if (!document.body.classList.contains('bt-nav-opened')) return;
                if (currentView === 'root') return;
                if (e.touches.length !== 1) return;

                const t = e.touches[0];

                // Оставляем системный Safari back-swipe самому iPhone.
                // Все, что правее — считаем нашей gesture-зоной.
                if (t.clientX < 18) return;

                touchStartX = t.clientX;
                touchStartY = t.clientY;
                touchCurrentX = t.clientX;
                touchTracking = true;
                touchLockedAxis = null;

                mobileNav.classList.add('is-swipe-dragging');
                mobileNav.style.setProperty('--bt-swipe-shift', '0px');
            },
            { passive: true }
        );

        gestureArea.addEventListener(
            'touchmove',
            (e) => {
                if (!touchTracking) return;
                if (e.touches.length !== 1) {
                    resetSwipeState();
                    return;
                }

                const t = e.touches[0];
                touchCurrentX = t.clientX;

                const dx = t.clientX - touchStartX;
                const dy = t.clientY - touchStartY;
                const absDx = Math.abs(dx);
                const absDy = Math.abs(dy);

                if (!touchLockedAxis) {
                    if (absDx < 6 && absDy < 6) return;
                    touchLockedAxis = absDx > absDy ? 'x' : 'y';
                }

                if (touchLockedAxis === 'y') {
                    resetSwipeState();
                    return;
                }

                e.preventDefault();

                if (dx <= 0) {
                    mobileNav.style.setProperty('--bt-swipe-shift', '0px');
                    return;
                }

                const shift = Math.min(dx, 110);
                mobileNav.style.setProperty('--bt-swipe-shift', `${shift}px`);
            },
            { passive: false }
        );

        gestureArea.addEventListener(
            'touchend',
            () => {
                if (!touchTracking) {
                    resetSwipeState();
                    return;
                }

                const totalDx = touchCurrentX - touchStartX;

                resetSwipeState();

                if (totalDx > 36) {
                    goBackToRoot();
                }
            },
            { passive: true }
        );

        gestureArea.addEventListener(
            'touchcancel',
            () => {
                resetSwipeState();
            },
            { passive: true }
        );
    }

    syncBackButton();
}